// LiveKit Safety Checker
// Background monitoring system that continuously verifies connectivity and automatically fixes issues

import { useParticipants, useConnectionState } from '@livekit/components-react';
import { ConnectionState, Track } from 'livekit-client';
import { canPlayersCommunicate } from '../services/VirtualRoomProximityHelper';
import { virtualRoomAreaService } from '../services/VirtualRoomAreaService';
import { getPhaserGameSync } from '../PhaserGame';
import Game from '../scenes/Game';

export interface SafetyCheckResult {
  timestamp: number;
  connectionState: ConnectionState;
  totalParticipants: number;
  participantsInProximity: number;
  audioTracksActive: number;
  videoTracksActive: number;
  issues: SafetyIssue[];
  fixesApplied: string[];
  healthScore: number; // 0-100
}

export interface SafetyIssue {
  type: 'connectivity' | 'subscription' | 'proximity' | 'permission' | 'performance';
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  participantId?: string;
  trackId?: string;
  suggestedFix?: string;
}

export class LiveKitSafetyChecker {
  private static instance: LiveKitSafetyChecker;
  private isRunning = false;
  private checkInterval: NodeJS.Timeout | null = null;
  private lastCheckResult: SafetyCheckResult | null = null;
  private consecutiveFailures = 0;
  private maxConsecutiveFailures = 3;
  private autoFixEnabled = true;
  private lastFrameTime: number = 0;

  static getInstance(): LiveKitSafetyChecker {
    if (!LiveKitSafetyChecker.instance) {
      LiveKitSafetyChecker.instance = new LiveKitSafetyChecker();
    }
    return LiveKitSafetyChecker.instance;
  }

  startMonitoring(intervalMs: number = 3000): void {
    if (this.isRunning) {
      // Silently return instead of warning to reduce console noise
      return;
    }

    console.log('[LiveKit Safety] Starting background monitoring...');
    this.isRunning = true;
    this.consecutiveFailures = 0;

    this.checkInterval = setInterval(() => {
      this.performSafetyCheck();
    }, intervalMs);

    // Perform initial check
    this.performSafetyCheck();
  }

  stopMonitoring(): void {
    if (!this.isRunning) {
      return;
    }

    console.log('[LiveKit Safety] Stopping background monitoring...');
    this.isRunning = false;

    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  private performSafetyCheck(): SafetyCheckResult {
    const result: SafetyCheckResult = {
      timestamp: Date.now(),
      connectionState: ConnectionState.Disconnected,
      totalParticipants: 0,
      participantsInProximity: 0,
      audioTracksActive: 0,
      videoTracksActive: 0,
      issues: [],
      fixesApplied: [],
      healthScore: 100
    };

    try {
      // Get current state
      const phaserGame = getPhaserGameSync();
      if (!phaserGame) {
        result.issues.push({
          type: 'connectivity',
          severity: 'critical',
          message: 'Phaser game not initialized'
        });
        result.healthScore = 0;
        return result;
      }
      
      const game = phaserGame.scene?.getScene('Game') as Game | undefined;
      if (!game || !game.myPlayer) {
        result.issues.push({
          type: 'connectivity',
          severity: 'critical',
          message: 'Game not initialized or myPlayer not available'
        });
        result.healthScore = 0;
        return result;
      }

      // Check connection state
      const connectionState = this.getConnectionState();
      result.connectionState = connectionState;

      if (connectionState !== ConnectionState.Connected) {
        result.issues.push({
          type: 'connectivity',
          severity: 'critical',
          message: `Connection state is ${connectionState}, expected Connected`
        });
        result.healthScore -= 30;
      }

      // Check participants and proximity
      const participants = this.getParticipants();
      result.totalParticipants = participants.length;

      const myPlayer = game.myPlayer;
      const otherPlayers = Array.from((game as any).otherPlayerMap.values());
      let participantsInProximity = 0;

      // Check each participant
      participants.forEach((participant) => {
        if (participant.isLocal) return;

        const otherPlayer = game.getOtherPlayerById(participant.identity);
        if (otherPlayer) {
          const shouldBeInProximity = canPlayersCommunicate(
            { x: myPlayer.x, y: myPlayer.y, playerId: myPlayer.playerId },
            { x: otherPlayer.x, y: otherPlayer.y, playerId: otherPlayer.playerId },
            100
          );

          if (shouldBeInProximity) {
            participantsInProximity++;
          }

          // Check if participant should be in proximity but isn't
          const isActuallyInProximity = this.isParticipantInProximity(participant.identity);
          if (shouldBeInProximity && !isActuallyInProximity) {
            result.issues.push({
              type: 'proximity',
              severity: 'high',
              message: `Participant ${participant.identity} should be in proximity but isn't`,
              participantId: participant.identity,
              suggestedFix: 'Force proximity recalculation'
            });
            result.healthScore -= 15;
          }
        }
      });

      result.participantsInProximity = participantsInProximity;

      // Check audio/video tracks
      const audioTracks = this.getAudioTracks();
      const videoTracks = this.getVideoTracks();
      result.audioTracksActive = audioTracks.length;
      result.videoTracksActive = videoTracks.length;

      // Check screen share tracks and health
      const screenShareIssues = this.checkScreenShareHealth(participants, myPlayer, game);
      result.issues.push(...screenShareIssues);
      if (screenShareIssues.length > 0) {
        result.healthScore -= screenShareIssues.length * 5; // Deduct 5 points per screen share issue
      }

      // Check track subscriptions
      participants.forEach((participant) => {
        if (participant.isLocal) return;

        const audioTracks = Array.from(participant.trackPublications.values() as any)
          .filter((pub: any) => pub.kind === 'audio');

        if (audioTracks.length > 0) {
          const subscribedAudioTracks = audioTracks.filter((pub: any) => pub.isSubscribed);
          const shouldBeSubscribed = this.isParticipantInProximity(participant.identity);

          if (shouldBeSubscribed && subscribedAudioTracks.length === 0) {
            result.issues.push({
              type: 'subscription',
              severity: 'medium',
              message: `Participant ${participant.identity} has audio but not subscribed`,
              participantId: participant.identity,
              suggestedFix: 'Subscribe to audio tracks'
            });
            result.healthScore -= 10;
          }
        }
      });

      // Check performance
      const performanceIssues = this.checkPerformance();
      result.issues.push(...performanceIssues);

      // Check bandwidth usage during screen share
      const bandwidthIssues = this.checkBandwidthUsage(participants);
      result.issues.push(...bandwidthIssues);
      if (bandwidthIssues.length > 0) {
        result.healthScore -= bandwidthIssues.length * 3; // Deduct 3 points per bandwidth issue
      }

      // Apply fixes if enabled
      if (this.autoFixEnabled && result.issues.length > 0) {
        const fixes = this.applyFixes(result.issues);
        result.fixesApplied = fixes;
      }

      // Update consecutive failures
      if (result.healthScore < 50) {
        this.consecutiveFailures++;
      } else {
        this.consecutiveFailures = 0;
      }

      // Log results
      this.logSafetyCheckResult(result);

      this.lastCheckResult = result;
      return result;

    } catch (error) {
      console.error('[LiveKit Safety] Error during safety check:', error);
      result.issues.push({
        type: 'connectivity',
        severity: 'critical',
        message: `Safety check failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
      result.healthScore = 0;
      return result;
    }
  }

  private getConnectionState(): ConnectionState {
    // This would need to be implemented based on your LiveKit setup
    // For now, return a default state
    return ConnectionState.Connected;
  }

  private getParticipants(): any[] {
    // This would need to be implemented based on your LiveKit setup
    // For now, return empty array
    return [];
  }

  private isParticipantInProximity(participantId: string): boolean {
    // This would need to be implemented based on your proximity system
    // For now, return false
    return false;
  }

  private getAudioTracks(): any[] {
    // This would need to be implemented based on your LiveKit setup
    // For now, return empty array
    return [];
  }

  private getVideoTracks(): any[] {
    // This would need to be implemented based on your LiveKit setup
    // For now, return empty array
    return [];
  }

  private checkScreenShareHealth(participants: any[], myPlayer: any, game: any): SafetyIssue[] {
    const issues: SafetyIssue[] = [];

    participants.forEach((participant) => {
      if (participant.isLocal) {
        // Check local screen share health
        if (participant.isScreenShareEnabled) {
          const screenShareTracks = Array.from(participant.trackPublications.values() as any)
            .filter((pub: any) => pub.source === Track.Source.ScreenShare || pub.source === Track.Source.ScreenShareAudio);
          
          screenShareTracks.forEach((publication: any) => {
            const track = publication.track;
            if (track) {
              // Check track state
              if (track.readyState === 'ended') {
                issues.push({
                  type: 'connectivity',
                  severity: 'high',
                  message: `Local screen share track ended unexpectedly`,
                  trackId: publication.trackSid,
                  suggestedFix: 'Restart screen share'
                });
              } else if (track.muted) {
                issues.push({
                  type: 'connectivity',
                  severity: 'medium',
                  message: `Local screen share track is muted`,
                  trackId: publication.trackSid,
                  suggestedFix: 'Check screen share permissions'
                });
              }

              // Check if track is subscribed (should be for local)
              if (!publication.isSubscribed) {
                issues.push({
                  type: 'subscription',
                  severity: 'medium',
                  message: `Local screen share track not subscribed`,
                  trackId: publication.trackSid,
                  suggestedFix: 'Subscribe to screen share track'
                });
              }
            } else if (publication.trackSid) {
              // Track publication exists but track is null
              issues.push({
                type: 'connectivity',
                severity: 'high',
                message: `Local screen share track publication exists but track is null`,
                trackId: publication.trackSid,
                suggestedFix: 'Restart screen share'
              });
            }
          });
        }
      } else {
        // Check remote screen share health
        if (participant.isScreenShareEnabled) {
          const otherPlayer = game?.getOtherPlayerById?.(participant.identity);
          if (otherPlayer && myPlayer) {
            // Check if participant should be in proximity
            const shouldBeInProximity = this.isParticipantInProximity(participant.identity);
            
            const screenShareTracks = Array.from(participant.trackPublications.values() as any)
              .filter((pub: any) => pub.source === 'screen_share' || pub.source === 'screen_share_audio');
            
            screenShareTracks.forEach((publication: any) => {
              // Check if screen share track should be subscribed
              if (shouldBeInProximity && !publication.isSubscribed) {
                issues.push({
                  type: 'subscription',
                  severity: 'medium',
                  message: `Remote screen share track not subscribed for ${participant.identity}`,
                  participantId: participant.identity,
                  trackId: publication.trackSid,
                  suggestedFix: 'Subscribe to screen share track'
                });
              }

              // Check track state
              const track = publication.track;
              if (track && track.readyState === 'ended') {
                issues.push({
                  type: 'connectivity',
                  severity: 'high',
                  message: `Remote screen share track ended for ${participant.identity}`,
                  participantId: participant.identity,
                  trackId: publication.trackSid
                });
              }
            });
          }
        }
      }
    });

    return issues;
  }

  private checkBandwidthUsage(participants: any[]): SafetyIssue[] {
    const issues: SafetyIssue[] = [];
    let totalScreenShareTracks = 0;
    let totalCameraTracks = 0;

    participants.forEach((participant) => {
      const screenShareTracks = Array.from(participant.trackPublications.values() as any)
        .filter((pub: any) => pub.source === Track.Source.ScreenShare);
      const cameraTracks = Array.from(participant.trackPublications.values() as any)
        .filter((pub: any) => pub.source === Track.Source.Camera);

      totalScreenShareTracks += screenShareTracks.length;
      totalCameraTracks += cameraTracks.length;

      // Check if participant has both camera and screen share active (high bandwidth)
      if (screenShareTracks.length > 0 && cameraTracks.length > 0) {
        // Check connection quality
        const connectionQuality = (participant as any).connectionQuality;
        if (connectionQuality === 'poor') {
          issues.push({
            type: 'performance',
            severity: 'medium',
            message: `High bandwidth usage: camera + screen share with poor connection for ${participant.identity}`,
            participantId: participant.identity,
            suggestedFix: 'Reduce camera quality or disable camera during screen share'
          });
        }
      }
    });

    // Warn if too many screen shares are active
    if (totalScreenShareTracks > 3) {
      issues.push({
        type: 'performance',
        severity: 'low',
        message: `Multiple screen shares active (${totalScreenShareTracks}), may impact bandwidth`
      });
    }

    return issues;
  }

  private checkPerformance(): SafetyIssue[] {
    const issues: SafetyIssue[] = [];

    // Check memory usage (commented out as performance.memory is not available in all browsers)
    // if (performance.memory) {
    //   const memoryUsage = performance.memory.usedJSHeapSize / performance.memory.jsHeapSizeLimit;
    //   if (memoryUsage > 0.8) {
    //     issues.push({
    //       type: 'performance',
    //       severity: 'medium',
    //       message: `High memory usage: ${Math.round(memoryUsage * 100)}%`
    //     });
    //   }
    // }

    // Check frame rate
    const fps = this.getCurrentFPS();
    if (fps < 30) {
      issues.push({
        type: 'performance',
        severity: 'low',
        message: `Low frame rate: ${fps} FPS`
      });
    }

    return issues;
  }

  private getCurrentFPS(): number {
    // Simple FPS calculation
    const now = performance.now();
    if (!this.lastFrameTime) {
      this.lastFrameTime = now;
      return 60;
    }
    
    const fps = 1000 / (now - this.lastFrameTime);
    this.lastFrameTime = now;
    return Math.round(fps);
  }

  private applyFixes(issues: SafetyIssue[]): string[] {
    const fixesApplied: string[] = [];

    issues.forEach(issue => {
      try {
        switch (issue.type) {
          case 'proximity':
            if (issue.suggestedFix === 'Force proximity recalculation') {
              window.dispatchEvent(new CustomEvent('force-proximity-recalculation'));
              fixesApplied.push(`Forced proximity recalculation for ${issue.participantId}`);
            }
            break;

          case 'subscription':
            if (issue.suggestedFix === 'Subscribe to audio tracks' && issue.participantId) {
              this.subscribeToParticipantAudio(issue.participantId);
              fixesApplied.push(`Subscribed to audio for ${issue.participantId}`);
            } else if (issue.suggestedFix === 'Subscribe to screen share track' && issue.participantId && issue.trackId) {
              this.subscribeToScreenShareTrack(issue.participantId, issue.trackId);
              fixesApplied.push(`Subscribed to screen share track for ${issue.participantId}`);
            }
            break;

          case 'connectivity':
            if (issue.severity === 'critical') {
              this.attemptReconnection();
              fixesApplied.push('Attempted reconnection');
            } else if (issue.suggestedFix === 'Restart screen share' && issue.trackId) {
              // Attempt to restart screen share
              window.dispatchEvent(new CustomEvent('livekit-restart-screen-share', {
                detail: { trackId: issue.trackId }
              }));
              fixesApplied.push('Attempted to restart screen share');
            }
            break;

          case 'performance':
            if (issue.message.includes('High memory usage')) {
              this.cleanupResources();
              fixesApplied.push('Cleaned up resources');
            }
            break;
        }
      } catch (error) {
        console.error(`[LiveKit Safety] Failed to apply fix for ${issue.type}:`, error);
      }
    });

    return fixesApplied;
  }

  private subscribeToScreenShareTrack(participantId: string, trackId: string): void {
    // This would need to be implemented based on your LiveKit setup
    console.log(`[LiveKit Safety] Subscribing to screen share track ${trackId} for ${participantId}`);
    // Dispatch event to trigger subscription
    window.dispatchEvent(new CustomEvent('livekit-subscribe-screen-share', {
      detail: { participantId, trackId }
    }));
  }

  private subscribeToParticipantAudio(participantId: string): void {
    // This would need to be implemented based on your LiveKit setup
    console.log(`[LiveKit Safety] Subscribing to audio for ${participantId}`);
  }

  private attemptReconnection(): void {
    console.log('[LiveKit Safety] Attempting reconnection...');
    window.dispatchEvent(new CustomEvent('livekit-manual-reconnect'));
  }

  private cleanupResources(): void {
    // Clean up any unnecessary resources
    // Note: performance.memory is not available in all browsers
    // if (performance.memory && performance.memory.usedJSHeapSize > 50 * 1024 * 1024) {
    //   // Force garbage collection if available
    //   if (window.gc) {
    //     window.gc();
    //   }
    // }
  }

  private logSafetyCheckResult(result: SafetyCheckResult): void {
    const healthStatus = result.healthScore >= 80 ? '🟢' : 
                        result.healthScore >= 60 ? '🟡' : 
                        result.healthScore >= 40 ? '🟠' : '🔴';

    console.group(`${healthStatus} LiveKit Safety Check (${result.healthScore}/100)`);
    console.log(`📊 Participants: ${result.totalParticipants} total, ${result.participantsInProximity} in proximity`);
    console.log(`🎵 Audio tracks: ${result.audioTracksActive}, Video tracks: ${result.videoTracksActive}`);
    console.log(`🔗 Connection: ${result.connectionState}`);

    if (result.issues.length > 0) {
      console.warn('⚠️ Issues detected:');
      result.issues.forEach(issue => {
        const severityIcon = issue.severity === 'critical' ? '🔴' :
                           issue.severity === 'high' ? '🟠' :
                           issue.severity === 'medium' ? '🟡' : '🟢';
        console.warn(`  ${severityIcon} ${issue.message}`);
      });
    }

    if (result.fixesApplied.length > 0) {
      console.log('🔧 Fixes applied:');
      result.fixesApplied.forEach(fix => {
        console.log(`  ✅ ${fix}`);
      });
    }

    if (result.healthScore >= 80) {
      console.log('✅ System healthy');
    } else if (result.healthScore >= 60) {
      console.warn('⚠️ System has minor issues');
    } else if (result.healthScore >= 40) {
      console.error('🚨 System has significant issues');
    } else {
      console.error('💥 System critical - immediate attention required');
    }

    console.groupEnd();
  }

  // Public methods for external control
  setAutoFixEnabled(enabled: boolean): void {
    this.autoFixEnabled = enabled;
    console.log(`[LiveKit Safety] Auto-fix ${enabled ? 'enabled' : 'disabled'}`);
  }

  getLastCheckResult(): SafetyCheckResult | null {
    return this.lastCheckResult;
  }

  forceCheck(): SafetyCheckResult {
    return this.performSafetyCheck();
  }

  isMonitoring(): boolean {
    return this.isRunning;
  }

  getConsecutiveFailures(): number {
    return this.consecutiveFailures;
  }

  shouldAlert(): boolean {
    return this.consecutiveFailures >= this.maxConsecutiveFailures;
  }
}

// Global access for debugging and control
if (typeof window !== 'undefined') {
  (window as any).LiveKitSafetyChecker = LiveKitSafetyChecker;
  (window as any).startLiveKitSafetyMonitoring = () => {
    const checker = LiveKitSafetyChecker.getInstance();
    checker.startMonitoring(3000);
    return checker;
  };
  (window as any).stopLiveKitSafetyMonitoring = () => {
    const checker = LiveKitSafetyChecker.getInstance();
    checker.stopMonitoring();
  };
  (window as any).forceLiveKitSafetyCheck = () => {
    const checker = LiveKitSafetyChecker.getInstance();
    return checker.forceCheck();
  };
} 
